package songwritersnotebook.database.composeApp

import app.cash.sqldelight.SuspendingTransacterImpl
import app.cash.sqldelight.db.AfterVersion
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlDriver
import app.cash.sqldelight.db.SqlSchema
import kotlin.Long
import kotlin.Unit
import kotlin.reflect.KClass
import songwritersnotebook.database.AppDatabase
import songwritersnotebook.database.AppDatabaseQueries
import songwritersnotebook.database.SongQueriesQueries

internal val KClass<AppDatabase>.schema: SqlSchema<QueryResult.AsyncValue<Unit>>
  get() = AppDatabaseImpl.Schema

internal fun KClass<AppDatabase>.newInstance(driver: SqlDriver): AppDatabase =
    AppDatabaseImpl(driver)

private class AppDatabaseImpl(
  driver: SqlDriver,
) : SuspendingTransacterImpl(driver), AppDatabase {
  override val appDatabaseQueries: AppDatabaseQueries = AppDatabaseQueries(driver)

  override val songQueriesQueries: SongQueriesQueries = SongQueriesQueries(driver)

  public object Schema : SqlSchema<QueryResult.AsyncValue<Unit>> {
    override val version: Long
      get() = 3

    override fun create(driver: SqlDriver): QueryResult.AsyncValue<Unit> = QueryResult.AsyncValue {
      driver.execute(null, """
          |CREATE TABLE SongEntity (
          |    id TEXT NOT NULL PRIMARY KEY,
          |    ownerId TEXT NOT NULL DEFAULT '',
          |    title TEXT NOT NULL,
          |    lastModified INTEGER NOT NULL,
          |    bpm INTEGER NOT NULL DEFAULT 120,
          |    isDeleted INTEGER NOT NULL DEFAULT 0,
          |    data TEXT NOT NULL
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE SongVersionEntity (
          |    id TEXT NOT NULL PRIMARY KEY,
          |    ownerId TEXT NOT NULL DEFAULT '',
          |    songId TEXT NOT NULL,
          |    timestamp INTEGER NOT NULL,
          |    versionNumber INTEGER NOT NULL,
          |    type TEXT NOT NULL,
          |    notes TEXT NOT NULL,
          |    data TEXT NOT NULL,
          |    FOREIGN KEY(songId) REFERENCES SongEntity(id) ON DELETE CASCADE
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE HarmonyChatMessageEntity (
          |    id TEXT NOT NULL PRIMARY KEY,
          |    songId TEXT NOT NULL,
          |    userId TEXT NOT NULL,
          |    timestamp INTEGER NOT NULL,
          |    text TEXT NOT NULL,
          |    isUser INTEGER NOT NULL,
          |    FOREIGN KEY(songId) REFERENCES SongEntity(id) ON DELETE CASCADE
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE RhymeWordEntity (
          |    word TEXT NOT NULL,
          |    phonemes TEXT NOT NULL,
          |    stressedSuffix TEXT NOT NULL,
          |    PRIMARY KEY (word, phonemes)
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE WaveformEntity (
          |    url TEXT NOT NULL,
          |    ownerId TEXT NOT NULL DEFAULT '',
          |    points TEXT NOT NULL,
          |    durationMillis INTEGER NOT NULL,
          |    PRIMARY KEY (url, ownerId)
          |)
          """.trimMargin(), 0).await()
      driver.execute(null,
          "CREATE INDEX idx_harmony_chat_song_user ON HarmonyChatMessageEntity(songId, userId)",
          0).await()
      driver.execute(null, "CREATE INDEX idx_rhyme_suffix ON RhymeWordEntity(stressedSuffix)",
          0).await()
    }

    private fun migrateInternal(
      driver: SqlDriver,
      oldVersion: Long,
      newVersion: Long,
    ): QueryResult.AsyncValue<Unit> = QueryResult.AsyncValue {
      if (oldVersion <= 1 && newVersion > 1) {
        driver.execute(null,
            "ALTER TABLE RhymeWordEntity ADD COLUMN stressedSuffix TEXT NOT NULL DEFAULT ''",
            0).await()
        driver.execute(null,
            "CREATE INDEX IF NOT EXISTS idx_rhyme_suffix ON RhymeWordEntity(stressedSuffix)",
            0).await()
      }
      if (oldVersion <= 2 && newVersion > 2) {
        driver.execute(null, "ALTER TABLE SongEntity ADD COLUMN ownerId TEXT NOT NULL DEFAULT ''",
            0).await()
        driver.execute(null,
            "ALTER TABLE SongVersionEntity ADD COLUMN ownerId TEXT NOT NULL DEFAULT ''", 0).await()
        driver.execute(null, "DROP TABLE IF EXISTS WaveformEntity", 0).await()
        driver.execute(null, """
            |CREATE TABLE WaveformEntity (
            |    url TEXT NOT NULL,
            |    ownerId TEXT NOT NULL DEFAULT '',
            |    points TEXT NOT NULL,
            |    durationMillis INTEGER NOT NULL,
            |    PRIMARY KEY (url, ownerId)
            |)
            """.trimMargin(), 0).await()
      }
    }

    override fun migrate(
      driver: SqlDriver,
      oldVersion: Long,
      newVersion: Long,
      vararg callbacks: AfterVersion,
    ): QueryResult.AsyncValue<Unit> = QueryResult.AsyncValue {
      var lastVersion = oldVersion

      callbacks.filter { it.afterVersion in oldVersion until newVersion }
      .sortedBy { it.afterVersion }
      .forEach { callback ->
        migrateInternal(driver, oldVersion = lastVersion, newVersion = callback.afterVersion +
          1).await()
        callback.block(driver)
        lastVersion = callback.afterVersion + 1
      }

      if (lastVersion < newVersion) {
        migrateInternal(driver, lastVersion, newVersion).await()
      }
    }
  }
}
